import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiProductV3 } from '@wix/da-papi-types';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { BiData, PackagePickerClickBiEvent } from '@wix/da-bi/pkg/events';
import { CoreTypes } from '../../../../../types';
import { type PackagePickerStoreSlice } from '../../../../../../../redux/packagePicker/types';

interface Props {
  className?: string;
  coreType: CoreTypes;
  pickerMode: PackagePickerStoreSlice['pickerMode'];
  alreadyHasSubscription?: boolean;
  monthlyPackageProduct: PapiProductV3;
  yearlyPackageProduct: PapiProductV3;
  isRecommended: boolean;
  isCurrent: boolean;
  isDisabled?: boolean;
  onSelect: (selected: number[]) => void;
}

const CardButton: FC<Props> = ({
  className,
  coreType,
  pickerMode,
  alreadyHasSubscription,
  monthlyPackageProduct,
  yearlyPackageProduct,
  isRecommended,
  isCurrent,
  onSelect,
  isDisabled,
}) => {
  const { t } = useTranslation();
  const isGift = pickerMode === 'gift';

  const isCurrentDisabled = isDisabled && isCurrent;

  const buttonTextKey = useMemo(() => {
    if (isGift) {
      return 'packagePicker.coreOffer.cta.gift';
    }
    if (isCurrentDisabled) {
      return 'packagePicker.coreOffer.currentPlan';
    }
    return isCurrent
      ? 'packagePicker.coreOffer.cta.extend'
      : isRecommended
        ? 'packagePicker.coreOffer.cta.upgradeNow'
        : 'packagePicker.coreOffer.cta.upgrade';
  }, [isGift, isRecommended, isCurrent, isCurrentDisabled]);

  const biCoreType = coreType === CoreTypes.CORE_ACCESS ? 'core' : coreType;
  let biButtonType = 'gift';
  if (pickerMode === 'buy') {
    biButtonType = 'select';
  }

  return (
    <BrandedButton
      className={className}
      size={'large'}
      variant={'core-orange'}
      onClick={() =>
        onSelect([
          monthlyPackageProduct.subproductId,
          yearlyPackageProduct.subproductId,
        ])
      }
      disabled={isDisabled}
      biData={BiData<PackagePickerClickBiEvent>({
        evid: 501,
        button_type: biButtonType as any,
        core_type: biCoreType,
        give_core_or_become_core: alreadyHasSubscription
          ? 'upgrade_core'
          : pickerMode === 'gift'
            ? 'give_core'
            : 'become_core',
      })}
    >
      {t(buttonTextKey)}
    </BrandedButton>
  );
};
CardButton.displayName = 'CardButton';

export default CardButton;
